'use client';
import { cn } from '@fshr/shared-react';
import { useEffect, useRef, ReactElement, ReactNode, ReactPortal, Ref } from 'react';
import { createPortal } from 'react-dom';
import ReactFocusLock from 'react-focus-lock';
import BevSafeText from '../bev-safe-text';

export type DialogClassNames = {
	container?: string;
	overlay?: string;
	dialog?: string;
};

interface DialogProps {
	isOpen: any;
	onClose: any;
	title: any;
	children?: any;
	showTitle?: boolean;
	showCloseButton?: boolean;
	position?: { top: number; left: number };
	isPopup?: boolean;
	id?: string;
	classNames?: DialogClassNames;
	contentClassName?: string;
	disablePortal?: boolean;
	containerRef?: Ref<HTMLDivElement>;
	panelRef?: Ref<HTMLDivElement>;
}

export default function Dialog(props: DialogProps): ReactElement | ReactPortal | null {
	const dialogRef = useRef<HTMLDivElement>(null);
	const closeButtonRef = useRef<HTMLButtonElement>(null);
	const {
		isOpen,
		onClose,
		title,
		children,
		showTitle,
		showCloseButton,
		position,
		isPopup = false,
		id,
		classNames,
		contentClassName,
		disablePortal = false,
		containerRef,
		panelRef,
	} = props;

	useEffect(() => {
		const handleKeyDown = (e: any) => {
			if (e.key === 'Escape') {
				onClose?.(e.key);
			}
		};

		if (isOpen) {
			setTimeout(() => closeButtonRef.current?.focus(), 0);
			document.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	if (classNames) {
		const anchoredDialog = (
			<ReactFocusLock>
				<div id={id} ref={containerRef} className={classNames.container}>
					<div className={classNames.overlay} />
					<div ref={panelRef} role="dialog" aria-modal="true" className={classNames.dialog}>
						<div className={cn('Dialog-content', contentClassName)}>{children as ReactNode}</div>
					</div>
				</div>
			</ReactFocusLock>
		);

		if (disablePortal) return anchoredDialog;
		return createPortal(anchoredDialog, document.body);
	}

	const dialogContent = (
		<ReactFocusLock>
			<div
				role="dialog"
				aria-modal="true"
				aria-labelledby="dialog-title"
				className={cn([
					isPopup
						? 'absolute z-50 top-0 left-0'
						: 'fixed inset-0 z-50 flex items-center justify-center bg-black/60 w-full',
				])}
				style={
					isPopup
						? {
								top: position?.top ?? 0,
								left: position?.left ?? 0,
							}
						: {}
				}
			>
				<div
					ref={dialogRef}
					className={cn([
						'bg-surface-raised rounded-2xl shadow-[0px_5px_15px_0px_rgba(3,3,3,0.12)]',
						'border border-border-subtle',
						isPopup ? '' : 'w-full h-full max-w-none max-h-none relative',
					])}
				>
					{showTitle ? (
						<h2 id="dialog-title" className="text-xl font-semibold mb-4">
							<BevSafeText>{title}</BevSafeText>
						</h2>
					) : null}

					<div className={cn([isPopup ? '' : 'w-full h-full overflow-auto'])}>{children}</div>

					{showCloseButton ? (
						<button
							ref={closeButtonRef}
							onClick={onClose}
							className="absolute top-3 right-3 text-gray-600 hover:text-gray-800 cursor-pointer"
							aria-label="Close dialog"
						>
							✕
						</button>
					) : null}
				</div>
			</div>
		</ReactFocusLock>
	);

	// Use a portal to render the child component without container restrictions
	return createPortal(dialogContent, document.body);
}
